import React, { useEffect, useState } from 'react';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import {
  getAssessmentAttemptAnswers,
  getAssessmentAttemptResult,
  getOpenAssessmentAttempt,
  recordAssessmentAnswer,
  startAssessmentAttempt,
  submitAssessmentAttempt
} from '../../services/academyAssessmentService';
import { getCurrentUserEnrollmentForCourse } from '../../services/academyEnrollmentService';

const { FiCheckCircle } = FiIcons;
const COURSE_KEY = 'psykososialt-arbeidsmiljo-praktisk-handling';
const EXPECTED_QUESTION_COUNT = 15;

function getErrorMessage(error) {
  const messages = {
    NO_AUTH_SESSION: 'Logg inn for å åpne kunnskapstesten.',
    NO_ACTIVE_LEARNER: 'Det finnes ingen aktiv Academy-deltaker knyttet til brukeren din.',
    COURSE_NOT_FOUND: 'Det godkjente kurset ble ikke funnet.',
    COURSE_VERSION_NOT_FOUND: 'Den godkjente kursversjonen ble ikke funnet.',
    NO_ACTIVE_ENROLLMENT: 'Du har ikke en aktiv påmelding til dette kurset.',
    ASSESSMENT_UNAVAILABLE: 'Den publiserte kunnskapstesten er ikke tilgjengelig eller har ufullstendig innhold.'
  };

  return messages[error?.code] || error?.message || 'En uventet feil oppstod.';
}

function isOpenAttemptConflict(error) {
  const errorText = [
    error?.code,
    error?.message,
    error?.details,
    error?.constraint
  ].filter(Boolean).join(' ').toLowerCase();

  return error?.code === '23505' ||
    errorText.includes('uq_hms_academy_assessment_attempts_one_open_1715728000000') ||
    (errorText.includes('duplicate key') && errorText.includes('in_progress'));
}

function orderAndValidateQuestions(assessment) {
  if (
    assessment?.assessmentKey !== 'avsluttende-kunnskapstest' ||
    !['published', 'active'].includes(assessment.status) ||
    !Array.isArray(assessment.questions) ||
    assessment.questions.length !== EXPECTED_QUESTION_COUNT
  ) {
    return null;
  }

  const questions = [...assessment.questions]
    .sort((first, second) => first.displayOrder - second.displayOrder);

  const valid = questions.every((question) => (
    typeof question.id === 'string' &&
    typeof question.prompt === 'string' &&
    ['single_choice', 'true_false'].includes(question.type) &&
    Array.isArray(question.options) &&
    question.options.length >= 2 &&
    question.options.every((option) => (
      typeof option.id === 'string' && typeof option.text === 'string'
    ))
  ));

  if (!valid) return null;

  return questions.map((question) => ({
    ...question,
    options: [...question.options].sort(
      (first, second) => first.displayOrder - second.displayOrder
    )
  }));
}

export default function CourseKnowledgeCheck({ assessment }) {
  const [enrollment, setEnrollment] = useState(null);
  const [enrollmentStatus, setEnrollmentStatus] = useState('loading');
  const [enrollmentError, setEnrollmentError] = useState(null);
  const [attemptId, setAttemptId] = useState(null);
  const [attemptNumber, setAttemptNumber] = useState(null);
  const [answers, setAnswers] = useState({});
  const [hasOpenAttempt, setHasOpenAttempt] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [phase, setPhase] = useState('intro');
  const [questionIndex, setQuestionIndex] = useState(0);
  const [busy, setBusy] = useState(false);
  const [savingAnswer, setSavingAnswer] = useState(false);
  const [error, setError] = useState(null);
  const [result, setResult] = useState(null);

  const questions = orderAndValidateQuestions(assessment);

  async function restoreAnswers(openAttempt, availableQuestions = questions) {
    const savedAnswers = await getAssessmentAttemptAnswers(openAttempt.id);
    const questionById = new Map(
      (availableQuestions || []).map((question) => [question.id, question])
    );

    return (Array.isArray(savedAnswers) ? savedAnswers : []).reduce((restored, answer) => {
      const question = questionById.get(answer.question_id);
      const optionExists = question?.options.some(
        (option) => option.id === answer.selected_option_id
      );

      if (question && optionExists && !restored[answer.question_id]) {
        restored[answer.question_id] = answer.selected_option_id;
      }

      return restored;
    }, {});
  }

  function setOpenAttempt(openAttempt, restoredAnswers) {
    setAttemptId(openAttempt.id);
    setAttemptNumber(openAttempt.attempt_number);
    setAnswers(restoredAnswers);
    setHasOpenAttempt(true);
    setPhase('intro');
    setQuestionIndex(0);
    setSubmitted(false);
    setResult(null);
  }

  useEffect(() => {
    let isCurrent = true;

    if (!questions || !assessment?.id) {
      setEnrollmentStatus('error');
      setEnrollmentError({ code: 'ASSESSMENT_UNAVAILABLE' });
      return () => {
        isCurrent = false;
      };
    }

    setEnrollment(null);
    setEnrollmentError(null);
    setEnrollmentStatus('loading');
    setAttemptId(null);
    setAttemptNumber(null);
    setAnswers({});
    setHasOpenAttempt(false);
    setPhase('intro');
    setQuestionIndex(0);
    setError(null);

    async function initializeKnowledgeCheck() {
      try {
        const currentEnrollment = await getCurrentUserEnrollmentForCourse({
          courseKey: COURSE_KEY,
          version: '1.0',
          languageCode: 'nb'
        });

        if (!isCurrent) return;

        setEnrollment(currentEnrollment);

        const openAttempt = await getOpenAssessmentAttempt({
          enrollmentId: currentEnrollment.enrollmentId,
          assessmentId: assessment.id
        });

        if (!isCurrent) return;

        if (openAttempt?.status === 'in_progress') {
          const restoredAnswers = await restoreAnswers(openAttempt, questions);
          if (!isCurrent) return;
          setOpenAttempt(openAttempt, restoredAnswers);
        }

        if (isCurrent) setEnrollmentStatus('ready');
      } catch (lookupError) {
        if (!isCurrent) return;
        setEnrollmentError(lookupError);
        setEnrollmentStatus('error');
      }
    }

    initializeKnowledgeCheck();

    return () => {
      isCurrent = false;
    };
  }, [
    assessment?.id,
    assessment?.assessmentKey,
    assessment?.status,
    assessment?.questions?.length
  ]);

  async function handleStart() {
    if (busy || !enrollment || !assessment?.id) return;

    setBusy(true);
    setError(null);

    try {
      const newAttemptId = await startAssessmentAttempt({
        enrollmentId: enrollment.enrollmentId,
        assessmentId: assessment.id
      });

      setAttemptId(newAttemptId);
      setAttemptNumber(null);
      setHasOpenAttempt(false);
      setAnswers({});
      setPhase('questions');
    } catch (startError) {
      if (!isOpenAttemptConflict(startError)) {
        setError(startError);
        setBusy(false);
        return;
      }

      try {
        const openAttempt = await getOpenAssessmentAttempt({
          enrollmentId: enrollment.enrollmentId,
          assessmentId: assessment.id
        });

        if (!openAttempt || openAttempt.status !== 'in_progress') {
          setError({
            message: 'En test kan allerede være startet. Oppdater siden og prøv igjen.'
          });
          setBusy(false);
          return;
        }

        const restoredAnswers = await restoreAnswers(openAttempt);
        setOpenAttempt(openAttempt, restoredAnswers);
      } catch (recoveryError) {
        setError({
          message: getErrorMessage(recoveryError) ||
            'En påbegynt kunnskapstest kunne ikke hentes. Oppdater siden og prøv igjen.'
        });
      }
    } finally {
      setBusy(false);
    }
  }

  function handleContinue() {
    const firstUnansweredIndex = questions.findIndex(
      (item) => !answers[item.id]
    );

    setQuestionIndex(
      firstUnansweredIndex === -1
        ? questions.length - 1
        : firstUnansweredIndex
    );
    setPhase('questions');
    setError(null);
  }

  async function handleAnswer(question, selectedOptionId) {
    setSavingAnswer(true);
    setError(null);

    try {
      await recordAssessmentAnswer({
        attemptId,
        questionId: question.id,
        selectedOptionIds: [selectedOptionId]
      });
      setAnswers((current) => ({
        ...current,
        [question.id]: selectedOptionId
      }));
    } catch (answerError) {
      setError(answerError);
    } finally {
      setSavingAnswer(false);
    }
  }

  async function loadResult(targetAttemptId = attemptId) {
    setBusy(true);
    setError(null);

    try {
      const attemptResult = await getAssessmentAttemptResult(targetAttemptId);
      setResult(attemptResult);
      setPhase('result');
    } catch (resultError) {
      setError(resultError);
      setPhase('result_error');
    } finally {
      setBusy(false);
    }
  }

  async function handleSubmit() {
    setBusy(true);
    setError(null);

    try {
      const submittedAttemptId = await submitAssessmentAttempt({ attemptId });
      setAttemptId(submittedAttemptId);
      setSubmitted(true);

      try {
        setResult(await getAssessmentAttemptResult(submittedAttemptId));
        setPhase('result');
      } catch (resultError) {
        setError(resultError);
        setPhase('result_error');
      }
    } catch (submissionError) {
      setError(submissionError);
    } finally {
      setBusy(false);
    }
  }

  if (enrollmentStatus === 'loading') {
    return (
      <section className="space-y-5">
        <p className="rounded-2xl border border-slate-200 bg-white p-5 text-slate-600">
          Kontrollerer aktiv påmelding …
        </p>
      </section>
    );
  }

  if (enrollmentStatus === 'error') {
    return (
      <section className="space-y-5">
        <div role="alert" className="rounded-xl border border-rose-200 bg-rose-50 p-4 text-sm text-rose-800">
          {getErrorMessage(enrollmentError)}
        </div>
      </section>
    );
  }

  const answeredCount = questions.filter((item) => answers[item.id]).length;
  const allAnswered = answeredCount === EXPECTED_QUESTION_COUNT;
  const question = questions?.[questionIndex];

  return (
    <section className="space-y-5">
      <header className="rounded-2xl border border-slate-200 bg-white p-6">
        <p className="text-xs font-bold uppercase tracking-wider text-blue-700">Slutt-test · {EXPECTED_QUESTION_COUNT} spørsmål</p>
        <h2 className="mt-2 text-2xl font-bold text-slate-900">Kunnskapstest</h2>
        <p className="mt-2 text-sm leading-6 text-slate-600">
          Testen må bestås for å fullføre kurset.
        </p>
        <p className="mt-2 text-sm leading-6 text-slate-600">
          Beståttgrense: {assessment.passThreshold} %. Maks forsøk: {assessment.maxAttempts}.
        </p>
      </header>

      {error && (
        <div role="alert" className="rounded-xl border border-rose-200 bg-rose-50 p-4 text-sm text-rose-800">
          {getErrorMessage(error)}
        </div>
      )}

      {phase === 'intro' && (
        <div className="rounded-2xl border border-slate-200 bg-white p-6">
          {hasOpenAttempt ? (
            <>
              <p className="font-semibold text-slate-900">
                Du har en påbegynt kunnskapstest.
              </p>
              <p className="mt-2 text-sm text-slate-600">
                {answeredCount} av {EXPECTED_QUESTION_COUNT} spørsmål er allerede besvart.
              </p>
              {attemptNumber !== null && (
                <p className="mt-2 text-sm text-slate-600">Forsøk {attemptNumber}</p>
              )}
              <button
                type="button"
                disabled={busy}
                onClick={handleContinue}
                className="mt-5 rounded-xl bg-blue-600 px-5 py-3 text-sm font-semibold text-white disabled:cursor-not-allowed disabled:opacity-50"
              >
                Fortsett kunnskapstest
              </button>
            </>
          ) : (
            <button
              type="button"
              disabled={busy}
              onClick={handleStart}
              className="rounded-xl bg-blue-600 px-5 py-3 text-sm font-semibold text-white disabled:cursor-not-allowed disabled:opacity-50"
            >
              {busy ? 'Starter testen …' : 'Start kunnskapstest'}
            </button>
          )}
        </div>
      )}

      {phase === 'questions' && question && (
        <>
          <fieldset key={question.id} className="rounded-2xl border border-slate-200 bg-white p-5">
            <legend className="px-1 text-xs font-bold uppercase tracking-wider text-blue-700">
              Spørsmål {questionIndex + 1} av {questions.length}
            </legend>
            <p className="mb-4 font-semibold leading-6 text-slate-900">{question.prompt}</p>
            <div className="space-y-2">
              {question.options.map((option) => {
                const selected = answers[question.id] === option.id;

                return (
                  <label
                    key={option.id}
                    className={`flex cursor-pointer items-start gap-3 rounded-xl border p-3 text-sm ${
                      selected ? 'border-blue-300 bg-blue-50' : 'border-slate-200'
                    }`}
                  >
                    <input
                      type="radio"
                      name={`question-${question.id}`}
                      value={option.id}
                      checked={selected}
                      disabled={savingAnswer}
                      onChange={() => handleAnswer(question, option.id)}
                      className="mt-0.5 min-h-0 accent-blue-600"
                    />
                    <span>{option.text}</span>
                  </label>
                );
              })}
            </div>
            {answers[question.id] && (
              <p className="mt-3 text-sm leading-6 text-slate-600">
                <SafeIcon icon={FiCheckCircle} className="mr-2 inline text-emerald-600" />
                Svar lagret.
              </p>
            )}
          </fieldset>

          <p className="text-sm text-slate-600">{answeredCount} av {questions.length} besvart</p>

          <div className="flex flex-wrap items-center gap-3">
            <button
              type="button"
              disabled={questionIndex === 0 || savingAnswer}
              onClick={() => setQuestionIndex((index) => index - 1)}
              className="rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm font-semibold text-slate-700 disabled:cursor-not-allowed disabled:opacity-50"
            >
              Forrige
            </button>
            {questionIndex < questions.length - 1 ? (
              <button
                type="button"
                disabled={!answers[question.id] || savingAnswer}
                onClick={() => setQuestionIndex((index) => index + 1)}
                className="rounded-xl bg-blue-600 px-5 py-3 text-sm font-semibold text-white disabled:cursor-not-allowed disabled:opacity-50"
              >
                Neste
              </button>
            ) : (
              <button
                type="button"
                disabled={!allAnswered || savingAnswer}
                onClick={() => setPhase('confirm')}
                className="rounded-xl bg-blue-600 px-5 py-3 text-sm font-semibold text-white disabled:cursor-not-allowed disabled:opacity-50"
              >
                Lever testen
              </button>
            )}
          </div>
        </>
      )}

      {phase === 'confirm' && (
        <div className="rounded-2xl border border-slate-200 bg-white p-6">
          <p className="font-semibold text-slate-900">Vil du levere testen nå?</p>
          <p className="mt-2 text-sm text-slate-600">Du kan ikke endre svar etter at testen er levert.</p>
          <div className="mt-5 flex flex-wrap items-center gap-3">
            <button
              type="button"
              disabled={busy}
              onClick={handleSubmit}
              className="rounded-xl bg-blue-600 px-5 py-3 text-sm font-semibold text-white disabled:cursor-not-allowed disabled:opacity-50"
            >
              {busy ? 'Leverer …' : 'Bekreft og lever testen'}
            </button>
            <button
              type="button"
              disabled={busy}
              onClick={() => setPhase('questions')}
              className="rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm font-semibold text-slate-700 disabled:opacity-50"
            >
              Tilbake til testen
            </button>
          </div>
        </div>
      )}

      {(phase === 'result' || phase === 'result_error') && (
        <>
          {result ? (
            <>
              <p className={`font-bold ${result.passed ? 'text-emerald-700' : 'text-rose-700'}`}>
                {result.passed ? 'Bestått' : 'Ikke bestått'} · {result.points_earned}/{result.max_points} · {result.score_percent} %
              </p>
              <p className="text-sm text-slate-600">
                Beståttgrense: {assessment.passThreshold} %
              </p>
              <p className="text-sm leading-6 text-slate-600">
                {result.passed
                  ? 'Du har bestått kunnskapstesten.'
                  : 'Du har ikke bestått kunnskapstesten.'}
              </p>
            </>
          ) : (
            <div className="rounded-2xl border border-slate-200 bg-white p-5">
              <p className="text-sm text-slate-700">Resultatet kunne ikke lastes.</p>
              <button
                type="button"
                disabled={busy}
                onClick={() => loadResult()}
                className="mt-3 rounded-xl border border-slate-200 px-4 py-2 text-sm font-semibold text-slate-700 disabled:opacity-50"
              >
                {busy ? 'Laster resultat …' : 'Last resultat på nytt'}
              </button>
            </div>
          )}
        </>
      )}
    </section>
  );
}